import React, { useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Stack,
  Box,
  Typography,
  Button,
  LinearProgress,
  Chip,
  Collapse,
  ButtonBase,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import {
  CheckCircle as VerifiedIcon,
  RadioButtonUnchecked as EmptyIcon,
  CloudUpload as UploadIcon,
  ExpandMore as ExpandIcon,
  Info as InfoIcon,
  Chat as ChatIcon,
  Description as DocIcon,
  InsertDriveFile as FileIcon,
  Warning as RevisionIcon,
} from "@mui/icons-material";
import { tokens } from "@/tokens";
import {
  useGetSupplierPortalQuery,
  useGetSupplierPortalQuestionsQuery,
  useGetSupplierPortalDocSlotsQuery,
  useAnswerSupplierPortalQuestionMutation,
  useUploadSupplierPortalDocumentMutation,
  useConfirmSupplierPortalDocumentUploadMutation,
  useGenerateFirstSalePortalTokenMutation,
  useGetSupplierPortalDocumentRequestsQuery,
  useSendPortalVerificationCodeMutation,
  useVerifyPortalCodeMutation,
} from "@/services/first-sale-portal";
import { useGetFirstSaleCaseQuery } from "@/services/first-sale";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";

// ─── Tab Config ───────────────────────────────────────────────────────

const TABS = [
  { id: "questions", label: "Quick Questions" },
  { id: "your_docs", label: "Middleman Documents" },
  { id: "factory_docs", label: "Factory Documents" },
];

// ─── Flag question → doc impact mapping ──────────────────────────────
// Maps each flag-driving questionId to the condition name it controls,
// a test for whether the answer triggers the flag, and a label
// explaining what additional documents are required.

type DocImpact = {
  condition: string;
  test: (answer: string) => boolean;
  label: string;
  docLabels: string[];
};

const QUESTION_DOC_IMPACT: Record<string, DocImpact[]> = {
  supply_chain_tiers: [
    {
      condition: "multipleMiddlemen",
      test: (a) => !a.startsWith("No,"),
      label: "Additional transaction documents are required for the intermediary tier",
      docLabels: [
        "Purchase Order — Intermediary to Factory",
        "Invoice — Intermediary to Middleman",
        "Payment Proof — Middleman to Intermediary",
      ],
    },
  ],
  goods_possession: [
    {
      condition: "backToBackFOB",
      test: (a) => !a.startsWith("Yes,"),
      label: "A Title & Risk of Loss Agreement is required when goods ship directly from factory",
      docLabels: ["Title & Risk of Loss Agreement"],
    },
  ],
  materials_provided: [
    {
      condition: "materialsProvided",
      test: (a) => !a.startsWith("No"),
      label: "Documentation for provided materials/assists is required",
      docLabels: [
        "Assist Invoices",
        "Assist Payment Proof",
        "Assist Cost Sheet",
      ],
    },
  ],
  factory_relationship: [
    {
      condition: "relatedParties",
      test: (a) => !a.startsWith("No,"),
      label: "Related party assessment documents are required — organization chart, financial statements, and follow-up questions will appear",
      docLabels: [
        "Organization Chart",
        "Factory Financial Statements (2024, 2025, Projected 2026)",
        "Parent / Consolidated Financial Statements (2024, 2025, Projected 2026)",
      ],
    },
  ],
  related_comparable_sales: [
    {
      condition: "factorySellsToUnrelated",
      test: (a) => a.startsWith("Yes, the factory") || a.startsWith("Yes, both"),
      label: "Comparable invoices from the factory to unrelated middlemen are required",
      docLabels: ["Comparable Invoices — Factory to Unrelated Middlemen"],
    },
    {
      condition: "middlemanBuysFromUnrelated",
      test: (a) => a.startsWith("Yes, the middleman") || a.startsWith("Yes, both"),
      label: "Comparable invoices from unrelated factories to the middleman are required",
      docLabels: ["Comparable Invoices — Middleman from Unrelated Factories"],
    },
  ],
};

// ─── Question Row ─────────────────────────────────────────────────────

const QuestionRow: React.FC<{
  question: any;
  index: number;
  onSelect: (questionId: string, answer: string) => void;
  isSubmitting: boolean;
  docSlots: any[];
  token: string;
  sessionToken?: string;
}> = ({ question, index, onSelect, isSubmitting, docSlots, token, sessionToken }) => {
  const impacts = QUESTION_DOC_IMPACT[question.questionId];

  // Filter to only the impacts whose test passes for the current answer
  const triggeredImpacts = impacts && question.answer
    ? impacts.filter((imp) => imp.test(question.answer))
    : [];


  return (
    <Box
      sx={{
        py: 2,
        borderBottom: `1px solid ${tokens.border.light}`,
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.25 }}>
        <Typography
          sx={{
            fontSize: tokens.fontSize.sm2,
            fontWeight: tokens.fontWeight.semibold,
            color: tokens.text.primary,
            flex: 1,
          }}
        >
          {index + 1}. {question.question}
        </Typography>
        {question.status === "needs_input" && (
          <Chip
            label="Needs your input"
            size="small"
            sx={{
              fontSize: tokens.fontSize["2xs"],
              height: 20,
              bgcolor: tokens.chip.amber.bg,
              color: tokens.chip.amber.text,
              borderRadius: tokens.radius.sm,
              fontWeight: tokens.fontWeight.semibold,
              ml: 1.5,
              flexShrink: 0,
            }}
          />
        )}
      </Stack>
      <Stack direction="row" flexWrap="wrap" gap={0.75}>
        {question.options.map((opt: string) => {
          const isSelected = question.answer === opt;
          return (
            <Chip
              key={opt}
              label={opt}
              size="small"
              disabled={isSubmitting}
              onClick={() => onSelect(question.questionId, opt)}
              sx={{
                fontSize: tokens.fontSize.xs,
                fontWeight: isSelected ? tokens.fontWeight.semibold : tokens.fontWeight.normal,
                cursor: "pointer",
                borderRadius: tokens.radius.sm,
                height: 28,
                ...(isSelected
                  ? {
                      bgcolor: tokens.brand.primary,
                      color: "#fff",
                      "&:hover": { bgcolor: tokens.brand.primaryHover },
                    }
                  : {
                      bgcolor: tokens.bg.muted,
                      color: tokens.text.body,
                      border: `1px solid ${tokens.border.default}`,
                      "&:hover": { bgcolor: tokens.bg.hover, borderColor: tokens.text.disabled },
                    }),
              }}
            />
          );
        })}
      </Stack>

      {/* Show triggered document requirements inline */}
      {triggeredImpacts.length > 0 && (
        <Box
          sx={{
            mt: 1.5,
            bgcolor: "#EFF6FF",
            border: "1px solid #BFDBFE",
            borderRadius: tokens.radius.md,
            p: 1.5,
          }}
        >
          {triggeredImpacts.map((imp, impIdx) => (
            <Box key={imp.condition} sx={{ mb: impIdx < triggeredImpacts.length - 1 ? 1.5 : 0 }}>
              <Stack direction="row" spacing={0.75} alignItems="center" mb={0.75}>
                <DocIcon sx={{ fontSize: 16, color: "#2563EB" }} />
                <Typography
                  sx={{
                    fontSize: tokens.fontSize.xs,
                    fontWeight: tokens.fontWeight.semibold,
                    color: "#1D4ED8",
                  }}
                >
                  {imp.label}
                </Typography>
              </Stack>

              {(() => {
                const matchingSlots = docSlots.filter((s: any) =>
                  imp.docLabels.some((label) => s.label === label)
                );
                return matchingSlots.length > 0 ? (
                  <Stack spacing={0.75}>
                    {matchingSlots.map((slot: any) => (
                      <PortalDocRow key={slot.slotId} doc={slot} token={token} sessionToken={sessionToken} />
                    ))}
                  </Stack>
                ) : (
                  <Stack spacing={0.5}>
                    {imp.docLabels.map((label) => (
                      <Stack key={label} direction="row" spacing={0.75} alignItems="center">
                        <EmptyIcon sx={{ fontSize: 14, color: "#93C5FD" }} />
                        <Typography sx={{ fontSize: tokens.fontSize.xs, color: "#1E40AF" }}>
                          {label}
                        </Typography>
                      </Stack>
                    ))}
                    <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: "#6B7280", mt: 0.5, fontStyle: "italic" }}>
                      These document slots will appear in the document tabs once your answer is saved.
                    </Typography>
                  </Stack>
                );
              })()}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─── Document Row ─────────────────────────────────────────────────────

const PortalDocRow: React.FC<{
  doc: any;
  token: string;
  sessionToken?: string;
}> = ({ doc, token, sessionToken }) => {
  const [expanded, setExpanded] = useState(doc.status === "revision_requested");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadDoc, { isLoading: uploading }] = useUploadSupplierPortalDocumentMutation();
  const [confirmUpload] = useConfirmSupplierPortalDocumentUploadMutation();

  const isUploaded = doc.status === "uploaded";
  const isRevisionRequested = doc.status === "revision_requested";
  const hasDocuments = doc.documents && doc.documents.length > 0;

  const statusIcon = isUploaded ? (
    <VerifiedIcon sx={{ fontSize: 20, color: tokens.status.success }} />
  ) : isRevisionRequested ? (
    <RevisionIcon sx={{ fontSize: 20, color: tokens.status.danger }} />
  ) : (
    <EmptyIcon sx={{ fontSize: 20, color: tokens.text.disabled }} />
  );

  const statusLabel = isUploaded ? "Uploaded" : isRevisionRequested ? "Revision Needed" : "Needed";
  const statusColor = isUploaded
    ? { bgcolor: tokens.chip.green.bg, color: tokens.chip.green.text }
    : isRevisionRequested
    ? { bgcolor: tokens.chip.red.bg, color: tokens.chip.red.text }
    : { bgcolor: tokens.chip.amber.bg, color: tokens.chip.amber.text };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const result = await uploadDoc({
        token,
        sessionToken,
        slotId: doc.slotId,
        filename: file.name,
        mimeType: file.type || "application/octet-stream",
        sizeBytes: file.size,
      }).unwrap();

      // Upload to S3 using the presigned URL
      if (result?.uploadUrl) {
        await fetch(result.uploadUrl, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type || "application/octet-stream" },
        });

        // Trigger language detection in the background
        if (result.docId) {
          confirmUpload({ token, sessionToken, docId: result.docId });
        }
      }
    } catch (err) {
      console.error("Upload failed:", err);
    }

    // Reset the input
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Box
      sx={{
        borderBottom: `1px solid ${tokens.border.light}`,
        "&:last-child": { borderBottom: "none" },
        ...(isRevisionRequested ? { bgcolor: `${tokens.status.danger}03` } : {}),
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept=".pdf,.png,.jpg,.jpeg"
        onChange={handleFileSelect}
      />

      <ButtonBase
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: "flex",
          width: "100%",
          px: 2,
          py: 1.75,
          textAlign: "left",
          "&:hover": { bgcolor: tokens.bg.hover },
          transition: "background-color 150ms ease",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1 }}>
          {statusIcon}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                sx={{
                  fontSize: tokens.fontSize.sm2,
                  fontWeight: tokens.fontWeight.semibold,
                  color: tokens.text.primary,
                }}
              >
                {doc.label}
              </Typography>
              <Chip
                label={statusLabel}
                size="small"
                sx={{
                  fontSize: tokens.fontSize["2xs"],
                  height: 18,
                  borderRadius: tokens.radius.sm,
                  fontWeight: tokens.fontWeight.semibold,
                  ...statusColor,
                }}
              />
            </Stack>
            <Typography
              sx={{
                fontSize: tokens.fontSize.xs,
                color: tokens.text.muted,
                mt: 0.25,
              }}
            >
              {doc.description}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            {isUploaded && hasDocuments && (
              <Typography
                sx={{
                  fontSize: tokens.fontSize.xs,
                  color: tokens.text.secondary,
                  maxWidth: 200,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {doc.documents[0].filename}
              </Typography>
            )}
            <ExpandIcon
              sx={{
                fontSize: 18,
                color: tokens.text.muted,
                transform: expanded ? "rotate(180deg)" : "none",
                transition: "transform 200ms ease",
              }}
            />
          </Stack>
        </Stack>
      </ButtonBase>

      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2, pt: 0, ml: 5 }}>
          {/* Review feedback banner */}
          {isRevisionRequested && doc.reviewFeedback && (
            <Box
              sx={{
                bgcolor: tokens.status.dangerBg,
                border: `1px solid ${tokens.status.danger}20`,
                borderRadius: tokens.radius.md,
                px: 1.5,
                py: 1,
                mb: 1.5,
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="flex-start">
                <RevisionIcon sx={{ fontSize: 14, color: tokens.status.danger, mt: 0.25 }} />
                <Box>
                  <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, color: tokens.status.danger, mb: 0.25 }}>
                    Revision Requested
                  </Typography>
                  <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.body }}>
                    {doc.reviewFeedback}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}

          {/* Requirements checklist */}
          <Typography
            sx={{
              fontSize: tokens.fontSize.xs,
              fontWeight: tokens.fontWeight.bold,
              color: tokens.text.primary,
              mb: 0.75,
            }}
          >
            Document must include:
          </Typography>
          <Stack spacing={0.5} sx={{ mb: 1.5 }}>
            {doc.requirements.map((req: string, i: number) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center">
                {isUploaded ? (
                  <VerifiedIcon sx={{ fontSize: 14, color: tokens.status.success }} />
                ) : (
                  <EmptyIcon sx={{ fontSize: 14, color: tokens.text.disabled }} />
                )}
                <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.body }}>
                  {req}
                </Typography>
              </Stack>
            ))}
          </Stack>

          {/* Importer note */}
          {doc.importerNote && (
            <Box
              sx={{
                bgcolor: tokens.status.infoBg,
                border: `1px solid ${tokens.status.info}20`,
                borderRadius: tokens.radius.md,
                px: 1.5,
                py: 1,
                mb: 1.5,
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="flex-start">
                <InfoIcon sx={{ fontSize: 14, color: tokens.status.info, mt: 0.25 }} />
                <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.body }}>
                  {doc.importerNote}
                </Typography>
              </Stack>
            </Box>
          )}

          {/* Existing file info */}
          {isUploaded && hasDocuments && !isRevisionRequested ? (
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <FileIcon sx={{ fontSize: 16, color: tokens.text.muted }} />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: tokens.fontSize.xs,
                      color: tokens.text.primary,
                      fontFamily: "monospace",
                    }}
                  >
                    {doc.documents[0].filename}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  startIcon={uploading ? <CircularProgress size={12} /> : <UploadIcon sx={{ fontSize: 14 }} />}
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    textTransform: "none",
                    fontSize: tokens.fontSize["2xs"],
                    fontWeight: tokens.fontWeight.semibold,
                    color: tokens.brand.primary,
                    border: `1px solid ${tokens.brand.primary}40`,
                    borderRadius: tokens.radius.sm,
                    px: 1.5,
                    ml: "auto !important",
                    "&:hover": { bgcolor: `${tokens.brand.primary}08` },
                  }}
                >
                  Upload New Version
                </Button>
              </Stack>
            </Box>
          ) : (
            /* Upload area */
            <Box
              onClick={() => !uploading && fileInputRef.current?.click()}
              sx={{
                border: `2px dashed ${isRevisionRequested ? tokens.status.danger : tokens.border.default}`,
                borderRadius: tokens.radius.md,
                bgcolor: isRevisionRequested ? `${tokens.status.danger}04` : tokens.bg.hover,
                py: 3,
                px: 2,
                textAlign: "center",
                cursor: uploading ? "default" : "pointer",
                transition: "all 150ms ease",
                "&:hover": uploading ? {} : {
                  borderColor: tokens.brand.primary,
                  bgcolor: `${tokens.brand.primary}08`,
                },
              }}
            >
              {uploading ? (
                <CircularProgress size={28} sx={{ mb: 0.5 }} />
              ) : (
                <UploadIcon sx={{ fontSize: 28, color: tokens.text.muted, mb: 0.5 }} />
              )}
              <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.secondary, mb: 0.25 }}>
                {uploading ? "Uploading..." : isRevisionRequested ? "Upload corrected version" : "Drag and drop your file here"}
              </Typography>
              <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>
                or click to browse (PDF, PNG, JPG up to 25 MB)
              </Typography>
              {!uploading && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<UploadIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    textTransform: "none",
                    fontSize: tokens.fontSize.xs,
                    fontWeight: tokens.fontWeight.semibold,
                    bgcolor: tokens.brand.primary,
                    borderRadius: tokens.radius.sm,
                    boxShadow: "none",
                    mt: 1.5,
                    px: 2.5,
                    py: 0.75,
                    "&:hover": { bgcolor: tokens.brand.primaryHover, boxShadow: "none" },
                  }}
                >
                  {isRevisionRequested ? "Upload Corrected Version" : "Upload File"}
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── Portal Auth Gate ────────────────────────────────────────────────

interface PortalAuthGateProps {
  token: string;
  caseName: string;
  onVerified: (sessionToken: string) => void;
}

const PortalAuthGate: React.FC<PortalAuthGateProps> = ({ token, caseName, onVerified }) => {
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  // 6-digit code input — same pattern as login 2FA
  const [codeDigits, setCodeDigits] = useState<string[]>(["", "", "", "", "", ""]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [sendCode, { isLoading: sending }] = useSendPortalVerificationCodeMutation();
  const [verifyCode, { isLoading: verifying }] = useVerifyPortalCodeMutation();

  const loading = sending || verifying;

  // Focus first code input when entering code step
  React.useEffect(() => {
    if (step === "code") {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
      setResendCooldown(30);
    }
  }, [step]);

  // Countdown timer for resend cooldown
  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleCodeChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const newDigits = [...codeDigits];
    newDigits[index] = digit;
    setCodeDigits(newDigits);
    setError(null);

    // Auto-advance to next input
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !codeDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length > 0) {
      const newDigits = [...codeDigits];
      for (let i = 0; i < 6; i++) {
        newDigits[i] = pasted[i] || "";
      }
      setCodeDigits(newDigits);
      const nextEmpty = newDigits.findIndex((d) => !d);
      inputRefs.current[nextEmpty === -1 ? 5 : nextEmpty]?.focus();
    }
  };

  const extractErrorMessage = (err: unknown): string => {
    if (err && typeof err === "object") {
      if ("message" in err && typeof (err as { message: unknown }).message === "string") return (err as { message: string }).message;
      if ("data" in err && typeof (err as { data: unknown }).data === "object") {
        const data = (err as { data: { message?: string } }).data;
        if (data?.message) return data.message;
      }
    }
    return "";
  };

  const handleSendCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError(null);
    try {
      await sendCode({ token, email }).unwrap();
      setStep("code");
      setCodeDigits(["", "", "", "", "", ""]);
    } catch (err: unknown) {
      const msg = extractErrorMessage(err).toLowerCase();
      if (msg.includes("not authorized")) {
        setError("This email is not authorized to access this portal. Please contact your importer to request access.");
      } else if (msg.includes("wait") || msg.includes("cooldown")) {
        setError("Please wait 30 seconds before requesting another code.");
      } else {
        setError("Unable to send code. Please check your email and try again.");
      }
    }
  };

  const handleVerify = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const code = codeDigits.join("");
    if (code.length !== 6) return;
    setError(null);
    try {
      const result = await verifyCode({ token, email, code }).unwrap();
      if (result?.verified && result?.sessionToken) {
        onVerified(result.sessionToken);
      }
    } catch (err: unknown) {
      const msg = extractErrorMessage(err).toLowerCase();
      if (msg.includes("too many")) {
        setError("Too many failed attempts. Please request a new code.");
      } else if (msg.includes("expired")) {
        setError("This code has expired. Please request a new one.");
      } else {
        setError("Invalid verification code. Please try again.");
      }
      setCodeDigits(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError(null);
    try {
      await sendCode({ token, email }).unwrap();
      setResendCooldown(30);
      setCodeDigits(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } catch {
      setError("Unable to resend code. Please try again.");
    }
  };

  const handleBackToEmail = () => {
    setStep("email");
    setCodeDigits(["", "", "", "", "", ""]);
    setError(null);
  };

  // ─── Code verification screen (matches login 2FA exactly) ──────────
  if (step === "code") {
    return (
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Box sx={{ width: { xs: "95%", sm: "70%" }, maxWidth: 440, p: { xs: 2, md: 0 } }}>
          <form onSubmit={handleVerify}>
            <Stack spacing={1}>
              <Box>
                <Typography
                  fontSize={tokens.fontSize.lg}
                  sx={{ color: tokens.text.secondary }}
                >
                  Check your email
                </Typography>
              </Box>
            </Stack>
            <Box sx={{ my: 2 }}>
              <Typography
                variant="body2"
                sx={{ color: tokens.text.secondary, mb: 3 }}
              >
                We sent a 6-digit verification code to <strong>{email}</strong>.
                Enter it below to continue.
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center">
                {codeDigits.map((digit, index) => (
                  <Box
                    key={index}
                    component="input"
                    ref={(el: HTMLInputElement | null) => {
                      inputRefs.current[index] = el;
                    }}
                    value={digit}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleCodeChange(index, e.target.value)
                    }
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                      handleCodeKeyDown(index, e)
                    }
                    onPaste={handleCodePaste}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    sx={{
                      width: 48,
                      height: 56,
                      textAlign: "center",
                      fontSize: "1.5rem",
                      fontWeight: 600,
                      border: "2px solid",
                      borderColor: digit ? "primary.main" : "grey.300",
                      borderRadius: 1,
                      outline: "none",
                      "&:focus": {
                        borderColor: "primary.main",
                        boxShadow: "0 0 0 2px rgba(11, 73, 75, 0.2)",
                      },
                    }}
                  />
                ))}
              </Stack>
              {error && (
                <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.status.danger, mt: 1.5, textAlign: "center" }}>
                  {error}
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              fullWidth
              sx={{
                mt: 2,
                py: 1.5,
                fontSize: tokens.fontSize.xl,
                fontWeight: tokens.fontWeight.semibold,
              }}
              disabled={codeDigits.join("").length !== 6 || loading}
              type="submit"
              color="primary"
              startIcon={loading ? <CircularProgress size={22} /> : null}
            >
              {loading ? "Verifying" : "Verify Code"}
            </Button>
            <Button
              variant="text"
              fullWidth
              sx={{ mt: 1 }}
              onClick={handleResend}
              disabled={resendCooldown > 0 || sending || loading}
            >
              {sending
                ? "Sending..."
                : resendCooldown > 0
                  ? `Resend code (${resendCooldown}s)`
                  : "Resend code"}
            </Button>
            <Button
              variant="text"
              fullWidth
              sx={{ mt: 0.5 }}
              onClick={handleBackToEmail}
              disabled={loading}
            >
              Use a different email
            </Button>
          </form>
        </Box>
      </Stack>
    );
  }

  // ─── Email input screen with landing ─────────────────────────────────
  return (
    <Stack
      direction="column"
      justifyContent="center"
      alignItems="center"
      height="100vh"
    >
      <Box sx={{ width: { xs: "95%", sm: "70%" }, maxWidth: 440, p: { xs: 2, md: 0 } }}>
        <form onSubmit={handleSendCode}>
          <Stack spacing={1}>
            <Box>
              <Typography
                fontSize={tokens.fontSize.lg}
                sx={{ color: tokens.text.secondary }}
              >
                First Sale Document Portal
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ my: 2 }}>
            <Typography
              variant="body2"
              sx={{ color: tokens.text.secondary, mb: 1.5 }}
            >
              This is a secure portal for exchanging trade documents related to <strong>{caseName}</strong>.
              Upload purchase orders, invoices, payment records, and other compliance documents
              directly to your importer's review team.
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: tokens.text.muted, mb: 3, fontSize: tokens.fontSize.xs }}
            >
              All uploads are encrypted and shared only with the parties who sent you this link.
            </Typography>
            <Stack spacing={2}>
              <Box
                component="input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setEmail(e.target.value);
                  setError(null);
                }}
                disabled={sending}
                sx={{
                  width: "100%",
                  height: 48,
                  px: 1.5,
                  fontSize: "1rem",
                  border: "2px solid",
                  borderColor: error ? "error.main" : "grey.300",
                  borderRadius: 1,
                  outline: "none",
                  "&:focus": {
                    borderColor: "primary.main",
                    boxShadow: "0 0 0 2px rgba(11, 73, 75, 0.2)",
                  },
                }}
              />
              {error && (
                <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.status.danger }}>
                  {error}
                </Typography>
              )}
            </Stack>
          </Box>
          <Button
            variant="contained"
            fullWidth
            sx={{
              mt: 2,
              py: 1.5,
              fontSize: tokens.fontSize.xl,
              fontWeight: tokens.fontWeight.semibold,
            }}
            disabled={!email || sending}
            type="submit"
            color="primary"
            startIcon={sending ? <CircularProgress size={22} /> : null}
          >
            {sending ? "Sending" : "Continue"}
          </Button>
        </form>
      </Box>
    </Stack>
  );
};

// ─── Main Portal Page ────────────────────────────────────────────────

const SupplierPortal: React.FC = () => {
  const { token, caseId } = useParams<{ token?: string; caseId?: string }>();
  const [activeTab, setActiveTab] = useState("questions");
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  const [portalSessionToken, setPortalSessionToken] = useState<string | null>(null);

  // Auth state for preview mode
  const { user } = useAppSelector((state) => state.auth);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const isTDAdmin = user?.userRole === roles.TDADMIN;
  const tenantVars = isTDAdmin && selectedTenant ? { tenantId: selectedTenant._id } : {};

  // In preview mode, fetch case to get its portalToken
  const isPreviewMode = !token && !!caseId;
  const { data: previewCase } = useGetFirstSaleCaseQuery(
    { caseId: caseId!, ...tenantVars },
    { skip: !isPreviewMode }
  );

  // Auto-generate token for preview if case doesn't have one
  const [generateToken] = useGenerateFirstSalePortalTokenMutation();
  React.useEffect(() => {
    if (isPreviewMode && previewCase && !previewCase.portalToken && !generatedToken) {
      generateToken({ caseId: caseId!, ...tenantVars })
        .unwrap()
        .then((result) => setGeneratedToken(result.token))
        .catch((err) => console.error("Failed to generate portal token:", err));
    }
  }, [isPreviewMode, previewCase, caseId, generatedToken]);

  const portalToken = token || previewCase?.portalToken || generatedToken || "";

  const { data: portalCase, isLoading: caseLoading } = useGetSupplierPortalQuery(
    { token: portalToken, sessionToken: portalSessionToken ?? undefined },
    { skip: !portalToken }
  );

  const needs2fa = !!portalCase?.portal2faEnabled && !portalSessionToken && !isPreviewMode;

  const { data: questions = [] } = useGetSupplierPortalQuestionsQuery(
    { token: portalToken, sessionToken: portalSessionToken ?? undefined },
    { skip: !portalToken || !portalCase || needs2fa }
  );

  const { data: docSlots = [] } = useGetSupplierPortalDocSlotsQuery(
    { token: portalToken, sessionToken: portalSessionToken ?? undefined },
    { skip: !portalToken || !portalCase || needs2fa }
  );

  const { data: documentRequests = [] } = useGetSupplierPortalDocumentRequestsQuery(
    { token: portalToken, sessionToken: portalSessionToken ?? undefined },
    { skip: !portalToken || !portalCase || needs2fa }
  );

  const [answerQuestion, { isLoading: answering }] = useAnswerSupplierPortalQuestionMutation();

  const yourDocs = useMemo(() => docSlots.filter((d: any) => d.tab === "your_docs"), [docSlots]);
  const factoryDocs = useMemo(() => docSlots.filter((d: any) => d.tab === "factory_docs"), [docSlots]);

  const yourUploaded = yourDocs.filter((d: any) => d.status === "uploaded").length;
  const factoryUploaded = factoryDocs.filter((d: any) => d.status === "uploaded").length;
  const totalDocs = docSlots.length;
  const totalUploaded = docSlots.filter((d: any) => d.status === "uploaded").length;
  const totalRevisions = docSlots.filter((d: any) => d.status === "revision_requested").length;
  const totalNeeded = totalDocs - totalUploaded;
  const progressPct = totalDocs > 0 ? Math.round((totalUploaded / totalDocs) * 100) : 0;

  const questionsNeedInput = questions.filter((q: any) => q.status === "needs_input").length;
  const questionsAnswered = questions.filter((q: any) => q.status === "answered").length;

  const handleSelectAnswer = async (questionId: string, answer: string) => {
    if (!portalToken) return;
    try {
      await answerQuestion({ token: portalToken, sessionToken: portalSessionToken ?? undefined, questionId, answer }).unwrap();
    } catch (err) {
      console.error("Failed to save answer:", err);
    }
  };

  if (caseLoading) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: tokens.bg.muted }}>
        <Box sx={{ maxWidth: 960, mx: "auto", px: 3, py: 4 }}>
          <Skeleton width={300} height={32} />
          <Skeleton width={500} height={24} sx={{ mt: 1 }} />
          <Skeleton height={120} sx={{ mt: 3, borderRadius: 2 }} />
        </Box>
      </Box>
    );
  }

  if (!portalToken || !portalCase) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: tokens.bg.muted, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Box sx={{ textAlign: "center" }}>
          <DocIcon sx={{ fontSize: 48, color: tokens.text.muted, mb: 1 }} />
          <Typography sx={{ fontSize: tokens.fontSize.lg, fontWeight: tokens.fontWeight.heavy, color: tokens.text.primary, mb: 0.5 }}>
            Portal Not Found
          </Typography>
          <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted }}>
            This link may be invalid or expired. Please contact your importer for a new link.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (portalCase.portal2faEnabled && !portalSessionToken && !isPreviewMode) {
    return (
      <PortalAuthGate
        token={portalToken}
        caseName={portalCase.productDescription || portalCase.caseNumber}
        onVerified={(st) => setPortalSessionToken(st)}
      />
    );
  }

  const tabsWithCounts = TABS.map((t) => {
    if (t.id === "questions" && questionsNeedInput > 0) {
      return { ...t, badge: questionsNeedInput };
    }
    if (t.id === "your_docs") {
      return { ...t, count: `${yourUploaded}/${yourDocs.length}` };
    }
    if (t.id === "factory_docs") {
      return { ...t, count: `${factoryUploaded}/${factoryDocs.length}` };
    }
    return t;
  });

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: tokens.bg.muted }}>
      {/* Navbar */}
      <Box
        sx={{
          bgcolor: tokens.bg.surface,
          borderBottom: `1px solid ${tokens.border.default}`,
          px: 3,
          height: 56,
          display: "flex",
          alignItems: "center",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: "100%", maxWidth: 960, mx: "auto" }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: tokens.radius.sm,
                bgcolor: tokens.brand.primary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DocIcon sx={{ fontSize: 16, color: "#fff" }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: tokens.fontSize.lg,
                  fontWeight: tokens.fontWeight.heavy,
                  color: tokens.text.primary,
                  lineHeight: 1.2,
                  letterSpacing: "-0.3px",
                }}
              >
                First Sale Portal
              </Typography>
              <Typography
                sx={{
                  fontSize: tokens.fontSize["2xs"],
                  color: tokens.text.muted,
                  lineHeight: 1,
                }}
              >
                Document Collection
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            {portalCase.middlemanName && (
              <>
                <Typography
                  sx={{
                    fontSize: tokens.fontSize.sm2,
                    fontWeight: tokens.fontWeight.semibold,
                    color: tokens.text.primary,
                  }}
                >
                  {portalCase.middlemanName}
                </Typography>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: tokens.radius.full,
                    bgcolor: `${tokens.brand.primary}15`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, color: tokens.brand.primary }}>
                    {portalCase.middlemanName.charAt(0)}
                  </Typography>
                </Box>
              </>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Main content */}
      <Box sx={{ maxWidth: 960, mx: "auto", px: 3, py: 3 }}>
        {/* Page header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2.5 }}>
          <Box>
            <Typography
              sx={{
                fontSize: "1.15rem",
                fontWeight: tokens.fontWeight.heavy,
                color: tokens.text.primary,
                letterSpacing: "-0.3px",
                mb: 0.25,
              }}
            >
              {portalCase.productDescription || `Case ${portalCase.caseNumber}`}
            </Typography>
            <Typography
              sx={{
                fontSize: tokens.fontSize.sm2,
                color: tokens.text.secondary,
              }}
            >
              PO #{portalCase.poNumber}
              {portalCase.middlemanName && portalCase.factoryName
                ? ` \u00B7 ${portalCase.middlemanName} \u2192 ${portalCase.factoryName}`
                : ""}
            </Typography>
          </Box>
          <Button
            startIcon={<ChatIcon sx={{ fontSize: 14 }} />}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontSize: tokens.fontSize.xs,
              fontWeight: tokens.fontWeight.semibold,
              borderColor: tokens.border.default,
              color: tokens.text.primary,
              borderRadius: tokens.radius.md,
              px: 2,
              py: 0.5,
              "&:hover": { bgcolor: tokens.bg.hover, borderColor: tokens.text.muted },
            }}
          >
            Contact Support
          </Button>
        </Stack>

        {/* Progress card */}
        <Box
          sx={{
            bgcolor: tokens.bg.surface,
            border: `1px solid ${tokens.border.default}`,
            borderRadius: tokens.radius.md,
            p: 2.5,
            mb: 3,
          }}
        >
          <Stack direction="row" spacing={4} alignItems="center">
            {/* Progress ring */}
            <Box sx={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: tokens.radius.full,
                  background: `conic-gradient(${progressPct === 100 ? tokens.status.success : tokens.brand.primary} ${progressPct * 3.6}deg, ${tokens.bg.muted} 0deg)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: tokens.radius.full,
                    bgcolor: tokens.bg.surface,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ fontSize: tokens.fontSize.lg, fontWeight: tokens.fontWeight.heavy, color: tokens.text.primary }}>
                    {progressPct}%
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Stats */}
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 1 }}>
                Document Progress
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progressPct}
                sx={{
                  height: 6,
                  borderRadius: tokens.radius.full,
                  bgcolor: tokens.bg.muted,
                  mb: 1,
                  "& .MuiLinearProgress-bar": {
                    bgcolor: progressPct === 100 ? tokens.status.success : tokens.brand.primary,
                    borderRadius: tokens.radius.full,
                  },
                }}
              />
              <Stack direction="row" spacing={3}>
                <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.secondary }}>
                  <Typography component="span" sx={{ fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>{totalUploaded}</Typography>/{totalDocs} uploaded
                </Typography>
                {totalRevisions > 0 && (
                  <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.status.danger, fontWeight: tokens.fontWeight.semibold }}>
                    {totalRevisions} revision{totalRevisions > 1 ? "s" : ""} needed
                  </Typography>
                )}
                {totalNeeded - totalRevisions > 0 && (
                  <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>
                    {totalNeeded - totalRevisions} remaining
                  </Typography>
                )}
                {questionsNeedInput > 0 && (
                  <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.status.warning, fontWeight: tokens.fontWeight.semibold }}>
                    {questionsNeedInput} question{questionsNeedInput > 1 ? "s" : ""} unanswered
                  </Typography>
                )}
              </Stack>
            </Box>
          </Stack>
        </Box>

        {/* Tabs */}
        <Box
          sx={{
            display: "inline-flex",
            gap: 0.5,
            mb: 2.5,
            bgcolor: tokens.bg.surface,
            border: `1px solid ${tokens.border.default}`,
            borderRadius: tokens.radius.md,
            p: 0.5,
          }}
        >
          {tabsWithCounts.map((tab) => {
            const isActive = tab.id === activeTab;
            return (
              <ButtonBase
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                sx={{
                  px: 2,
                  py: 0.75,
                  fontSize: tokens.fontSize.sm2,
                  fontWeight: tokens.fontWeight.semibold,
                  borderRadius: tokens.radius.sm,
                  transition: "all 150ms ease",
                  display: "flex",
                  gap: 0.75,
                  alignItems: "center",
                  ...(isActive
                    ? {
                        color: tokens.text.primary,
                        bgcolor: tokens.bg.muted,
                        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                      }
                    : {
                        color: tokens.text.secondary,
                        "&:hover": {
                          color: tokens.text.body,
                          bgcolor: tokens.bg.hover,
                        },
                      }),
                }}
              >
                {tab.label}
                {"badge" in tab && (tab as any).badge > 0 && (
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 18,
                      height: 18,
                      borderRadius: tokens.radius.full,
                      bgcolor: tokens.chip.amber.bg,
                      color: tokens.chip.amber.text,
                      fontSize: tokens.fontSize["2xs"],
                      fontWeight: tokens.fontWeight.bold,
                    }}
                  >
                    {(tab as any).badge}
                  </Box>
                )}
                {"count" in tab && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: tokens.fontSize["2xs"],
                      color: tokens.text.muted,
                      fontWeight: tokens.fontWeight.semibold,
                    }}
                  >
                    {(tab as any).count}
                  </Typography>
                )}
              </ButtonBase>
            );
          })}
        </Box>

        {/* Document Requests Banner */}
        {documentRequests.length > 0 && (
          <Box
            sx={{
              mb: 2,
              bgcolor: tokens.bg.surface,
              border: `2px solid ${tokens.status.warning}`,
              borderRadius: tokens.radius.md,
              overflow: "hidden",
            }}
          >
            <Box sx={{ px: 2.5, py: 1.5, bgcolor: `${tokens.status.warning}10`, borderBottom: `1px solid ${tokens.status.warning}30` }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <RevisionIcon sx={{ fontSize: 18, color: tokens.status.warning }} />
                <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>
                  {documentRequests.length} Document{documentRequests.length > 1 ? "s" : ""} Requested
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, mt: 0.25 }}>
                Your importer has requested the following documents. Please review and upload.
              </Typography>
            </Box>
            <Stack spacing={0}>
              {documentRequests.map((req: any) => {
                const matchingSlot = docSlots.find((s: any) => s.slotId === req.slotId);
                return (
                  <Box
                    key={req._id}
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      borderBottom: `1px solid ${tokens.border.light}`,
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          bgcolor: `${tokens.status.warning}15`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          mt: 0.25,
                        }}
                      >
                        <RevisionIcon sx={{ fontSize: 14, color: tokens.status.warning }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.semibold, color: tokens.text.primary }}>
                          {req.label}
                        </Typography>
                        <Box sx={{ mt: 0.5, p: 1, bgcolor: `${tokens.status.danger}06`, borderRadius: tokens.radius.sm, border: `1px solid ${tokens.status.danger}15` }}>
                          <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.status.danger, fontWeight: tokens.fontWeight.semibold, mb: 0.25 }}>
                            Reason:
                          </Typography>
                          <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.body, lineHeight: 1.5 }}>
                            {req.reason}
                          </Typography>
                        </Box>
                        {req.requirements?.length > 0 && (
                          <Box sx={{ mt: 0.75 }}>
                            <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, fontWeight: tokens.fontWeight.semibold, mb: 0.25 }}>
                              Requirements:
                            </Typography>
                            {req.requirements.map((r: string, idx: number) => (
                              <Typography key={idx} sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.body, pl: 1 }}>
                                {"\u2022"} {r}
                              </Typography>
                            ))}
                          </Box>
                        )}
                        {matchingSlot && (
                          <Box sx={{ mt: 1 }}>
                            <PortalDocRow doc={matchingSlot} token={portalToken} sessionToken={portalSessionToken ?? undefined} />
                          </Box>
                        )}
                        {!matchingSlot && (
                          <Box sx={{ mt: 1 }}>
                            <PortalDocRow
                              doc={{
                                slotId: `custom_${req._id}`,
                                label: req.label,
                                description: req.description || "Custom document request",
                                tab: "your_docs",
                                requirements: req.requirements || [],
                                status: "needed",
                                importerNote: null,
                                reviewFeedback: null,
                                documents: [],
                              }}
                              token={portalToken}
                              sessionToken={portalSessionToken ?? undefined}
                            />
                          </Box>
                        )}
                        <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.disabled, mt: 0.5 }}>
                          Requested by {req.requestedBy}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Tab content */}
        {activeTab === "questions" && (
          <Box
            sx={{
              bgcolor: tokens.bg.surface,
              border: `1px solid ${tokens.border.default}`,
              borderRadius: tokens.radius.md,
              px: 2.5,
              py: 0.5,
            }}
          >
            {questionsNeedInput > 0 && (
              <Box
                sx={{
                  bgcolor: tokens.chip.amber.bg,
                  borderRadius: tokens.radius.sm,
                  px: 1.5,
                  py: 1,
                  mt: 1.5,
                  mb: 0.5,
                }}
              >
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <InfoIcon sx={{ fontSize: 14, color: tokens.chip.amber.text }} />
                  <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.chip.amber.text, fontWeight: tokens.fontWeight.semibold }}>
                    {questionsNeedInput} question{questionsNeedInput > 1 ? "s" : ""} still need your response. Please select the best answer.
                  </Typography>
                </Stack>
              </Box>
            )}
            {questions.map((q: any, i: number) => (
              <QuestionRow key={q._id || q.questionId} question={q} index={i} onSelect={handleSelectAnswer} isSubmitting={answering} docSlots={docSlots} token={portalToken} sessionToken={portalSessionToken ?? undefined} />
            ))}
            {questions.length > 0 && (
              <Box sx={{ py: 1.5, textAlign: "right" }}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={questionsNeedInput > 0}
                  sx={{
                    textTransform: "none",
                    fontSize: tokens.fontSize.xs,
                    fontWeight: tokens.fontWeight.semibold,
                    bgcolor: tokens.brand.primary,
                    borderRadius: tokens.radius.sm,
                    boxShadow: "none",
                    px: 3,
                    py: 0.75,
                    "&:hover": { bgcolor: tokens.brand.primaryHover, boxShadow: "none" },
                    "&.Mui-disabled": { bgcolor: tokens.bg.muted, color: tokens.text.disabled },
                  }}
                >
                  {questionsNeedInput > 0
                    ? `${questionsAnswered}/${questions.length} Answered`
                    : "All Questions Answered"}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {activeTab === "your_docs" && (
          <Box
            sx={{
              bgcolor: tokens.bg.surface,
              border: `1px solid ${tokens.border.default}`,
              borderRadius: tokens.radius.md,
              overflow: "hidden",
            }}
          >
            {yourDocs.map((doc: any) => (
              <PortalDocRow key={doc.slotId} doc={doc} token={portalToken} sessionToken={portalSessionToken ?? undefined} />
            ))}
          </Box>
        )}

        {activeTab === "factory_docs" && (
          <>
            {/* Instruction banner */}
            <Box
              sx={{
                bgcolor: tokens.status.infoBg,
                border: `1px solid ${tokens.status.info}20`,
                borderRadius: tokens.radius.sm,
                px: 2,
                py: 1.25,
                mb: 2,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <InfoIcon sx={{ fontSize: 16, color: tokens.status.info }} />
                <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.body }}>
                  Collect these documents from your factory and upload them here. If your factory can upload directly, share the portal link with them.
                </Typography>
              </Stack>
            </Box>

            <Box
              sx={{
                bgcolor: tokens.bg.surface,
                border: `1px solid ${tokens.border.default}`,
                borderRadius: tokens.radius.md,
                overflow: "hidden",
              }}
            >
              {factoryDocs.map((doc: any) => (
                <PortalDocRow key={doc.slotId} doc={doc} token={portalToken} sessionToken={portalSessionToken ?? undefined} />
              ))}
            </Box>
          </>
        )}

        {/* Need Help footer */}
        <Box
          sx={{
            mt: 4,
            mb: 2,
            p: 2.5,
            bgcolor: tokens.bg.surface,
            border: `1px solid ${tokens.border.default}`,
            borderRadius: tokens.radius.md,
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: tokens.fontSize.sm2,
              fontWeight: tokens.fontWeight.bold,
              color: tokens.text.primary,
              mb: 0.25,
            }}
          >
            Need Help?
          </Typography>
          <Typography
            sx={{
              fontSize: tokens.fontSize.xs,
              color: tokens.text.muted,
              mb: 1.5,
            }}
          >
            Contact your importer's trade compliance team for guidance on document requirements.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default SupplierPortal;
